/** Shared mutation factory: invalidation + feedback handling by construction.
 *
 * Most feature mutations repeat the same shape — call the API, invalidate
 * some query keys, report success/error via `apiErrorMessage`. Routing them
 * through this factory keeps error handling consistent (no silent failures,
 * no raw Error.message toasts) and trims per-feature boilerplate.
 *
 * Success stays a lightweight `message.success` toast; failures use a
 * `notification.error` that stays long enough to be read, carries the
 * parsed RFC-7807 reason as its body, and is announced to screen readers
 * (`role="alert"` → assertive aria-live).
 *
 * Use for new mutations (e.g. edit drawers); migrate existing hooks as their
 * call sites are touched — call sites that already toast must drop their own
 * messages when they migrate, or the user sees doubles.
 */

import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from "@tanstack/react-query";
import { App as AntdApp } from "antd";
import type { MessageInstance } from "antd/es/message/interface";
import type { NotificationInstance } from "antd/es/notification/interface";
import { isValidElement, type ReactNode } from "react";

import { apiErrorMessage } from "@/lib/errors";

/** How long error notifications stay up. Long, but not for ever: the
 *  platform test of 2026-10-12 filed four failed requests as "no error shown"
 *  because the 3s `message.error` (and the 8s notification this used to be)
 *  had gone by the time the tester looked, so round 1 made them stay until
 *  closed — and the next test found them piled over the form that had to be
 *  corrected, taking its clicks (M-23). The clock stops while the pointer is
 *  on the notification, and `ThemedApp` puts them on the side of the screen
 *  drawers do not open on. */
export const ERROR_NOTIFICATION_SECONDS = 12;

/** One notification per failed action: a second failure of the same action
 *  replaces the first instead of stacking under it. */
function errorKey(title: string): string {
  return `mutation-error:${title}`;
}

/**
 * Report a failed mutation the standard way: `notification.error` with the
 * parsed RFC-7807 reason as body and `role="alert"` (assertive aria-live), so
 * screen readers announce it. `useInvalidatingMutation` routes through this
 * automatically; call it directly only where the hook doesn't fit — e.g. a
 * DrawerForm `onFinish` try/catch that must return `false` to keep the drawer
 * open, or an imperative flow outside TanStack Query. Never use a bare
 * `message.error` for mutation failures: it is not announced and vanishes in
 * 3 seconds.
 *
 *   const { notification } = AntdApp.useApp();
 *   ...
 *   } catch (error) {
 *     notifyMutationError(notification, "Failed to create bucket", error);
 *     return false;
 *   }
 */
export function notifyMutationError(
  notification: NotificationInstance,
  title: string,
  error: unknown,
): void {
  const description = apiErrorMessage(error, title);
  notification.error({
    message: title,
    // apiErrorMessage falls back to the title when the response carries no
    // detail — don't repeat it as the body.
    description: description === title ? undefined : description,
    key: errorKey(title),
    duration: ERROR_NOTIFICATION_SECONDS,
    pauseOnHover: true,
    // Announced immediately by screen readers (assertive aria-live).
    role: "alert",
  });
}

/**
 * Make every `message.error` raised through `AntdApp.useApp()` a failure the
 * user can still read when they look: the same long-lived,
 * `role="alert"` notification as `notifyMutationError`, instead of a toast
 * that is gone in 3 seconds and has no close button. Mounted once in
 * `ThemedApp`, so the ~340 call sites that still toast by hand are covered
 * without each migrating first; `message.success` / `info` / `warning` are
 * untouched. Returns the function that puts the original back.
 */
export function keepErrorToasts(
  message: MessageInstance,
  notification: NotificationInstance,
): () => void {
  const original = message.error;
  message.error = (content, duration, onClose) => {
    // The object form (`{ content, key, … }`) is unused in this codebase, and
    // a caller that names its own duration or close handler has opted out.
    const isArgs =
      typeof content === "object" &&
      content !== null &&
      !isValidElement(content) &&
      "content" in content;
    if (isArgs || duration !== undefined || onClose !== undefined) {
      return original(content, duration, onClose);
    }
    const body = content as ReactNode;
    const key =
      typeof body === "string" ? errorKey(body) : `mutation-error:${Date.now()}`;
    notification.error({
      message: body,
      key,
      duration: ERROR_NOTIFICATION_SECONDS,
      pauseOnHover: true,
      role: "alert",
    });
    const close = () => notification.destroy(key);
    // `message.error` returns a closer that is also awaitable.
    return Object.assign(close, {
      then: (resolve?: (value: boolean) => unknown) =>
        Promise.resolve(true).then(resolve),
    }) as ReturnType<MessageInstance["error"]>;
  };
  return () => {
    message.error = original;
  };
}

interface InvalidatingMutationOptions<TData, TVariables> {
  /** Query-key prefixes to invalidate on success. */
  invalidates: readonly QueryKey[];
  /** Toast on success; omit for silent mutations. */
  successMessage?: string | ((data: TData, variables: TVariables) => string);
  /** Short action label ("Failed to create volume") — used as the error
   *  notification's title and as the fallback when the API error carries no
   *  usable detail. */
  errorMessage?: string;
  onSuccess?: (data: TData, variables: TVariables) => void;
}

export function useInvalidatingMutation<TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  {
    invalidates,
    successMessage,
    errorMessage,
    onSuccess,
  }: InvalidatingMutationOptions<TData, TVariables>,
): UseMutationResult<TData, Error, TVariables> {
  const queryClient = useQueryClient();
  const { message, notification } = AntdApp.useApp();
  return useMutation<TData, Error, TVariables>({
    mutationFn,
    onSuccess: (data, variables) => {
      for (const key of invalidates) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      if (successMessage) {
        message.success(
          typeof successMessage === "function"
            ? successMessage(data, variables)
            : successMessage,
        );
      }
      // The same action went through: its earlier failure no longer stands.
      notification.destroy(errorKey(errorMessage ?? "Request failed"));
      onSuccess?.(data, variables);
    },
    onError: (error) => {
      notifyMutationError(notification, errorMessage ?? "Request failed", error);
    },
  });
}
